import React, { useEffect, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Loader2, Gift, Copy, Check, Users, Send } from "lucide-react";
import { GlassButton } from "@/components/GlassBits";

export const UserReferrals = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    userApi.get("/user/referrals")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "No se pudo cargar tu enlace")));
  }, []);

  const copy = async () => {
    await navigator.clipboard.writeText(data.link);
    setCopied(true);
    toast.success("Enlace copiado");
    setTimeout(() => setCopied(false), 2200);
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-red-400" />;
  const share = `Únete a Digital Forge conmigo y edita tus imágenes con IA: ${data.link}`;

  return (
    <div className="space-y-6" data-testid="user-referrals">
      <div className="glass-card forge-card p-6">
        <div className="flex items-center gap-2 text-white font-semibold">
          <Gift className="w-5 h-5 text-amber-300" /> Invita y gana
        </div>
        <p className="text-sm text-white/65 mt-2 leading-relaxed">
          Comparte tu enlace. Cuando tu amigo entre y haga su <strong>primera edición</strong>,
          ganan <strong>{data.credits_per_friend} créditos cada uno</strong>.
          Puedes premiar hasta {data.max_rewarded} amigos.
        </p>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <div className="flex-1 min-w-0 glass-card px-4 py-3 font-mono text-xs text-amber-200 break-all flex items-center"
            data-testid="referral-link">{data.link}</div>
          <div className="sm:w-44 shrink-0">
            <GlassButton tone="red" icon={copied ? Check : Copy} onClick={copy}
              data-testid="copy-referral-btn">{copied ? "Copiado" : "Copiar"}</GlassButton>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <a href={`https://wa.me/?text=${encodeURIComponent(share)}`} target="_blank" rel="noreferrer"
            data-testid="share-whatsapp-btn"
            className="text-xs px-4 py-2 rounded-full border border-white/12 text-white/80 hover:border-amber-300/50 hover:text-white transition-colors">
            Compartir por WhatsApp
          </a>
          <a href={`https://t.me/share/url?url=${encodeURIComponent(data.link)}&text=${encodeURIComponent("Únete a Digital Forge conmigo")}`}
            target="_blank" rel="noreferrer" data-testid="share-telegram-btn"
            className="text-xs px-4 py-2 rounded-full border border-white/12 text-white/80 hover:border-amber-300/50 hover:text-white transition-colors flex items-center gap-1.5">
            <Send className="w-3.5 h-3.5" /> Compartir por Telegram
          </a>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["Amigos invitados", data.invited.length],
          ["Premiados", `${data.rewarded_count}/${data.max_rewarded}`],
          ["Créditos ganados", data.credits_earned]].map(([label, value]) => (
          <div key={label} className="glass-card p-4 text-center">
            <div className="text-2xl font-bold text-white" data-testid={`referral-stat-${label}`}>{value}</div>
            <div className="text-[11px] text-white/50 mt-1 uppercase tracking-wider">{label}</div>
          </div>
        ))}
      </div>

      {data.invited.length > 0 && (
        <div>
          <div className="text-white font-semibold mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-amber-300" /> Tus invitados
          </div>
          <div className="space-y-2">
            {data.invited.map((i, idx) => (
              <div key={idx} className="glass-card px-4 py-3 flex items-center justify-between"
                data-testid={`referral-row-${idx}`}>
                <div>
                  <div className="text-sm text-white">{i.name}</div>
                  <div className="text-[11px] text-white/45">{i.email}</div>
                </div>
                <span className={`text-xs ${i.rewarded ? "text-emerald-400" : "text-white/45"}`}>
                  {i.rewarded ? `+${data.credits_per_friend} créditos` : "Falta su primera edición"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserReferrals;
